import React, { useState, useEffect } from 'react';
import { Clock, ArrowLeft, CheckCircle, Award, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

const EmployeeTrainingInterface = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('login');
  const [employee, setEmployee] = useState(null);
  const [modules, setModules] = useState([]);
  const [currentAssessment, setCurrentAssessment] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [progress, setProgress] = useState({ answered: 0, total: 0, current: 1 });
  const [showExplanation, setShowExplanation] = useState(false);
  const [assessmentResults, setAssessmentResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const TRAINING_API_BASE = '/api/documents/training';

  const LoginForm = () => {
    const [pin, setPin] = useState('');
    const [salonCode, setSalonCode] = useState('');

    const handleLogin = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log('Attempting employee login...');
        
        const response = await fetch(`${TRAINING_API_BASE}/employee/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ pin, salon_code: salonCode })
        });
        
        const data = await response.json();
        console.log('Login response:', data);
        
        if (data.success) {
          setEmployee(data.employee);
          setCurrentStep('modules');
          await loadModules();
        } else {
          setError(data.error || 'Login failed');
        }
      } catch (error) {
        console.error('Login error:', error);
        setError('Connection error. Please check your internet connection.');
      }
      setLoading(false);
    };

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
          padding: '32px',
          width: '100%',
          maxWidth: '448px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <button
              onClick={() => navigate('/documents')}
              style={{
                padding: '8px',
                borderRadius: '50%',
                background: '#f3f4f6',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#e5e7eb'}
              onMouseOut={(e) => e.target.style.background = '#f3f4f6'}
            >
              <Home size={20} color="#6b7280" />
            </button>
            <div style={{ fontSize: '32px' }}>🎓</div>
            <div style={{ width: '40px' }}></div>
          </div>
          
          <div style={{
            textAlign: 'center',
            marginBottom: '32px'
          }}>
            <h1 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '8px',
              margin: '0 0 8px 0'
            }}>Salon Training</h1>
            <p style={{
              color: '#6b7280',
              margin: 0
            }}>Enter your details to access training</p>
          </div>
          
          {error && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <p style={{
                color: '#dc2626',
                fontSize: '14px',
                margin: 0
              }}>{error}</p>
            </div>
          )}
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>Training PIN</label>
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  outline: 'none',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="Enter your 6-digit PIN"
                maxLength={6}
              />
            </div>
            
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>Salon Code</label>
              <input
                type="text"
                value={salonCode}
                onChange={(e) => setSalonCode(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  outline: 'none',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="Enter salon name or code"
              />
            </div>
            
            <button
              onClick={handleLogin}
              disabled={loading || !pin || !salonCode}
              style={{
                width: '100%',
                background: loading || !pin || !salonCode 
                  ? '#9ca3af' 
                  : 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: loading || !pin || !salonCode ? 'not-allowed' : 'pointer',
                border: 'none',
                transition: 'all 0.3s',
                opacity: loading || !pin || !salonCode ? 0.5 : 1
              }}
              onMouseOver={(e) => {
                if (!loading && pin && salonCode) {
                  e.target.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                }
              }}
              onMouseOut={(e) => {
                e.target.style.boxShadow = 'none';
              }}
            >
              {loading ? 'Logging in...' : 'Start Training'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const loadModules = async () => {
    try {
      console.log('Loading training modules...');
      const response = await fetch(`${TRAINING_API_BASE}/modules`, {
        credentials: 'include'
      });
      const data = await response.json();
      console.log('Modules response:', data);
      
      if (data.success) {
        setModules(data.modules || []);
      } else {
        setError('Failed to load training modules');
      }
    } catch (error) {
      console.error('Failed to load modules:', error);
      setError('Failed to load training modules');
    }
  };

  const ModulesList = () => {
    const startModule = async (moduleId) => {
      try {
        console.log('Starting module:', moduleId);
        const response = await fetch(`${TRAINING_API_BASE}/start/${moduleId}`, {
          method: 'POST',
          credentials: 'include'
        });
        const data = await response.json();
        console.log('Start assessment response:', data);
        
        if (data.success) {
          setCurrentAssessment(data);
          setCurrentStep('assessment');
          await loadFirstQuestion(data.assessment_id);
        } else {
          setError(data.error || 'Failed to start assessment');
        }
      } catch (error) {
        console.error('Failed to start module:', error);
        setError('Failed to start assessment');
      }
    };

    return (
      <div style={{
        minHeight: '100vh',
        background: '#f9fafb',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{
          background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
          color: 'white',
          padding: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <button 
              onClick={() => setCurrentStep('login')}
              style={{
                padding: '8px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                marginRight: '16px',
                border: 'none',
                cursor: 'pointer',
                color: 'white',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
              onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                margin: '0 0 4px 0'
              }}>{employee?.salon_name || 'Training Portal'}</h1>
              <p style={{
                opacity: 0.9,
                margin: 0
              }}>Welcome, {employee?.first_name} {employee?.last_name}</p>
            </div>
          </div>
        </div>

        <div style={{ padding: '24px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '24px',
            margin: '0 0 24px 0'
          }}>Training Modules</h2>
          
          {error && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{
                color: '#dc2626',
                margin: 0
              }}>{error}</p>
            </div>
          )}
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {modules.length > 0 ? (
              modules.map((module) => (
                <div 
                  key={module.id} 
                  style={{
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    padding: '24px'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '16px'
                  }}>
                    <div>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#1f2937',
                        margin: '0 0 4px 0'
                      }}>{module.title}</h3>
                      <p style={{
                        color: '#6b7280',
                        fontSize: '14px',
                        marginTop: '4px',
                        margin: 0
                      }}>{module.description}</p>
                    </div>
                    <span style={{
                      padding: '6px 12px',
                      background: '#dbeafe',
                      color: '#1e40af',
                      fontSize: '12px',
                      fontWeight: '500',
                      borderRadius: '9999px'
                    }}>
                      {module.package_level || 'basic'}
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>
                      <span>⏱️ {module.time_estimate || 15} mins</span>
                      <span>❓ {module.questions_count || 10} questions</span>
                      <span>🎯 {module.passing_score || 70}% to pass</span>
                    </div>
                    
                    {module.completed_at ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <CheckCircle color="#10b981" size={20} />
                        <span style={{
                          color: '#059669',
                          fontWeight: '500'
                        }}>{module.score}%</span>
                        {module.certificate_number && (
                          <Award color="#f59e0b" size={20} />
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => startModule(module.id)}
                        style={{
                          background: '#3b82f6',
                          color: 'white',
                          padding: '8px 24px',
                          borderRadius: '8px',
                          fontWeight: '500',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.background = '#2563eb'}
                        onMouseOut={(e) => e.target.style.background = '#3b82f6'}
                      >
                        {module.latest_assessment_id ? 'Resume' : 'Start'}
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '48px 0'
              }}>
                <div style={{
                  color: '#9ca3af',
                  fontSize: '60px',
                  marginBottom: '16px'
                }}>📚</div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '8px',
                  margin: '0 0 8px 0'
                }}>No Training Modules Available</h3>
                <p style={{
                  color: '#6b7280',
                  margin: 0
                }}>Please contact your manager to assign training modules.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const loadFirstQuestion = async (assessmentId) => {
    try {
      console.log('Loading question for assessment:', assessmentId);
      const response = await fetch(`${TRAINING_API_BASE}/question/${assessmentId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      console.log('Question response:', data);
      
      if (data.success) {
        if (data.completed) {
          await completeAssessment(assessmentId);
        } else {
          setCurrentQuestion(data.question);
          setProgress(data.progress || { answered: 0, total: 10, current: 1 });
          setCurrentStep('question');
          setSelectedAnswer(null);
          setShowExplanation(false);
        }
      } else {
        setError(data.error || 'Failed to load question');
      }
    } catch (error) {
      console.error('Failed to load question:', error);
      setError('Failed to load question');
    }
  };

  const submitAnswer = async () => {
    if (!selectedAnswer) return;

    try {
      console.log('Submitting answer:', selectedAnswer);
      const response = await fetch(`${TRAINING_API_BASE}/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          assessment_id: currentAssessment.assessment_id,
          question_id: currentQuestion.id,
          answer: selectedAnswer,
          time_taken: 30
        })
      });
      
      const data = await response.json();
      console.log('Answer submission response:', data);
      
      if (data.success) {
        setShowExplanation(true);
        setTimeout(() => {
          loadFirstQuestion(currentAssessment.assessment_id);
        }, 3000);
      } else {
        setError(data.error || 'Failed to submit answer');
      }
    } catch (error) {
      console.error('Failed to submit answer:', error);
      setError('Failed to submit answer');
    }
  };

  const completeAssessment = async (assessmentId) => {
    try {
      console.log('Completing assessment:', assessmentId);
      const response = await fetch(`${TRAINING_API_BASE}/complete/${assessmentId}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      console.log('Assessment completion response:', data);
      
      if (data.success) {
        setAssessmentResults(data);
        setCurrentStep('results');
      } else {
        setError(data.error || 'Failed to complete assessment');
      }
    } catch (error) {
      console.error('Failed to complete assessment:', error);
      setError('Failed to complete assessment');
    }
  };

  const QuestionView = () => {
    const renderAnswerOptions = () => {
      if (!currentQuestion || !currentQuestion.options) {
        return (
          <div style={{
            textAlign: 'center',
            padding: '32px 0',
            color: '#6b7280'
          }}>
            Loading answer options...
          </div>
        );
      }

      return currentQuestion.options.map((option, index) => (
        <button
          key={option.value || index}
          onClick={() => setSelectedAnswer(option.value)}
          style={{
            width: '100%',
            padding: '16px',
            textAlign: 'left',
            border: selectedAnswer === option.value ? '2px solid #3b82f6' : '2px solid #e5e7eb',
            borderRadius: '12px',
            background: selectedAnswer === option.value ? '#eff6ff' : 'white',
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginBottom: '12px'
          }}
          onMouseOver={(e) => {
            if (selectedAnswer !== option.value) {
              e.target.style.borderColor = '#93c5fd';
              e.target.style.background = '#f9fafb';
            }
          }}
          onMouseOut={(e) => {
            if (selectedAnswer !== option.value) {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.background = 'white';
            }
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: '#3b82f6',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px',
              flexShrink: 0
            }}>
              {option.value || String.fromCharCode(65 + index)}
            </div>
            <span style={{ color: '#1f2937' }}>{option.text}</span>
          </div>
        </button>
      ));
    };

    return (
      <div style={{
        minHeight: '100vh',
        background: 'white',
        fontFamily: 'Arial, sans-serif',
        paddingBottom: '80px'
      }}>
        <div style={{
          background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
          color: 'white',
          padding: '16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <button 
              onClick={() => setCurrentStep('modules')}
              style={{
                padding: '8px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                cursor: 'pointer',
                color: 'white',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
              onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
            >
              <ArrowLeft size={20} />
            </button>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: '600' }}>{employee?.salon_name}</div>
              <div style={{
                fontSize: '14px',
                opacity: 0.9
              }}>{currentAssessment?.module?.title}</div>
            </div>
            <div style={{ width: '40px' }}></div>
          </div>
        </div>

        <div style={{
          padding: '16px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <span style={{
              fontSize: '14px',
              color: '#6b7280'
            }}>
              Question {progress.current} of {progress.total}
            </span>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              color: '#ea580c',
              fontSize: '14px'
            }}>
              <Clock size={16} style={{ marginRight: '4px' }} />
              Time remaining
            </div>
          </div>
          <div style={{
            width: '100%',
            background: '#e5e7eb',
            borderRadius: '9999px',
            height: '8px'
          }}>
            <div 
              style={{
                background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                height: '8px',
                borderRadius: '9999px',
                transition: 'width 0.3s',
                width: `${(progress.answered / progress.total) * 100}%`
              }}
            />
          </div>
        </div>

        {error && (
          <div style={{
            padding: '16px',
            background: '#fef2f2',
            borderBottom: '1px solid #fecaca'
          }}>
            <p style={{
              color: '#dc2626',
              fontSize: '14px',
              margin: 0
            }}>{error}</p>
          </div>
        )}

        <div style={{ padding: '24px' }}>
          <div style={{
            background: '#3b82f6',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '9999px',
            fontSize: '12px',
            fontWeight: '600',
            display: 'inline-block',
            marginBottom: '16px'
          }}>
            Question {progress.current}
          </div>
          
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '24px',
            lineHeight: '1.6',
            margin: '0 0 24px 0'
          }}>
            {currentQuestion?.question_text || 'Loading question...'}
          </h2>

          <div style={{ marginBottom: '24px' }}>
            {renderAnswerOptions()}
          </div>

          {showExplanation && currentQuestion?.explanation && (
            <div style={{
              background: '#fff7ed',
              borderLeft: '4px solid #fb923c',
              padding: '16px',
              borderRadius: '0 8px 8px 0',
              marginBottom: '24px'
            }}>
              <div style={{
                fontWeight: '600',
                color: '#9a3412',
                marginBottom: '8px'
              }}>💡 Explanation</div>
              <p style={{
                color: '#c2410c',
                fontSize: '14px',
                margin: 0
              }}>{currentQuestion.explanation}</p>
            </div>
          )}
        </div>

        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'white',
          borderTop: '1px solid #e5e7eb',
          padding: '16px'
        }}>
          <div style={{
            display: 'flex',
            gap: '12px',
            maxWidth: '448px',
            margin: '0 auto'
          }}>
            <button
              onClick={() => setCurrentStep('modules')}
              style={{
                flex: 1,
                padding: '12px 16px',
                background: '#f3f4f6',
                color: '#6b7280',
                borderRadius: '8px',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#e5e7eb'}
              onMouseOut={(e) => e.target.style.background = '#f3f4f6'}
            >
              ← Exit
            </button>
            <button
              onClick={showExplanation ? () => loadFirstQuestion(currentAssessment.assessment_id) : submitAnswer}
              disabled={!selectedAnswer && !showExplanation}
              style={{
                flex: 1,
                padding: '12px 16px',
                background: (!selectedAnswer && !showExplanation) 
                  ? '#9ca3af' 
                  : 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
                color: 'white',
                borderRadius: '8px',
                fontWeight: '500',
                border: 'none',
                cursor: (!selectedAnswer && !showExplanation) ? 'not-allowed' : 'pointer',
                opacity: (!selectedAnswer && !showExplanation) ? 0.5 : 1,
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                if (selectedAnswer || showExplanation) {
                  e.target.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                }
              }}
              onMouseOut={(e) => {
                e.target.style.boxShadow = 'none';
              }}
            >
              {showExplanation ? 'Next →' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ResultsView = () => {
    const downloadCertificate = async () => {
      if (!assessmentResults?.certificate_number) return;
      
      try {
        const response = await fetch(`${TRAINING_API_BASE}/certificate/${assessmentResults.certificate_number}`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `certificate_${assessmentResults.certificate_number}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      } catch (error) {
        console.error('Failed to download certificate:', error);
      }
    };

    return (
      <div style={{
        minHeight: '100vh',
        background: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
          padding: '32px',
          width: '100%',
          maxWidth: '448px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '60px',
            marginBottom: '24px'
          }}>
            {assessmentResults?.passed ? '🎉' : '📚'}
          </div>
          
          <h1 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '16px',
            margin: '0 0 16px 0'
          }}>
            Assessment {assessmentResults?.passed ? 'Complete!' : 'Incomplete'}
          </h1>
          
          <div style={{
            fontSize: '40px',
            fontWeight: 'bold',
            color: '#3b82f6',
            marginBottom: '16px'
          }}>
            {assessmentResults?.score || 0}%
          </div>
          
          <p style={{
            color: '#6b7280',
            marginBottom: '24px',
            margin: '0 0 24px 0'
          }}>
            {assessmentResults?.passed 
              ? 'Excellent work! You\'ve successfully completed this training module.'
              : `You need ${assessmentResults?.passing_score || 70}% to pass. You can retake this assessment.`
            }
          </p>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {assessmentResults?.certificate_number && (
              <button 
                onClick={downloadCertificate}
                style={{
                  width: '100%',
                  background: '#10b981',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  fontWeight: '500',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#059669'}
                onMouseOut={(e) => e.target.style.background = '#10b981'}
              >
                📜 Download Certificate
              </button>
            )}
            
            <button
              onClick={() => setCurrentStep('modules')}
              style={{
                width: '100%',
                background: '#f3f4f6',
                color: '#6b7280',
                padding: '12px 16px',
                borderRadius: '8px',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#e5e7eb'}
              onMouseOut={(e) => e.target.style.background = '#f3f4f6'}
            >
              Back to Training Modules
            </button>
            
            <button
              onClick={() => navigate('/documents')}
              style={{
                width: '100%',
                background: '#3b82f6',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '8px',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#2563eb'}
              onMouseOut={(e) => e.target.style.background = '#3b82f6'}
            >
              Return to Main Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  switch (currentStep) {
    case 'login':
      return <LoginForm />;
    case 'modules':
      return <ModulesList />;
    case 'question':
      return <QuestionView />;
    case 'results':
      return <ResultsView />;
    default:
      return <LoginForm />;
  }
};

export default EmployeeTrainingInterface;